import {
  ChakraFlex,
  ChakraVStack,
  coalescePlainTextToEditorV2Html,
  EditorV2,
  EditorV2State,
  FAIconName,
  FireflyButton,
  SectionCard,
  Text,
} from '@equip.health/ui';
import { GetDischargePlanResponse } from '~/store/services/clinical-data/dischargePlan';
import { useCallback, useRef } from 'react';
import { getDischargeStatuses } from '../util/dischargePlanUtils';
import { Section } from '~/components/maud/inquiries/InquiryDetail/sections';
import {
  AFTERCARE_AND_REFERRAL_FIELDS,
  AftercareAndReferralsSectionField,
} from './section-cards';

interface ReferralsAndFollowUpAppointmentsSectionProps {
  dischargePlanData?: GetDischargePlanResponse;
  onSelectField: (fieldName: AftercareAndReferralsSectionField | null) => void;
}

const field = {
  id: 'referrals-and-follow-up-appointments',
  key: 'referralsAndFollowUpAppointments' as const,
};

const ReferralsAndFollowUpAppointmentsSection = ({
  dischargePlanData,
  onSelectField,
}: ReferralsAndFollowUpAppointmentsSectionProps) => {
  const editorRef = useRef<EditorV2State>(null);

  const { isSigned } = getDischargeStatuses(dischargePlanData?.status);
  const value = dischargePlanData?.[field.key];
  const hasFieldValue = !!value;
  const htmlContent = value ? coalescePlainTextToEditorV2Html(value) : '';

  const handleSelectField = useCallback(() => {
    onSelectField(
      AFTERCARE_AND_REFERRAL_FIELDS.find(
        (field) => field.key === 'referralsAndFollowUpAppointments',
      ) ?? null,
    );
  }, [onSelectField]);

  return (
    <Section id="referrals-follow-up-appointments">
      <ChakraFlex alignItems="center" justifyContent="space-between">
        <Text id="referrals-follow-up-appointments-header" variant="h5">
          Referrals & follow-up appointments
        </Text>
        {!isSigned && hasFieldValue && (
          <FireflyButton
            ariaLabel={`edit-${field.id}`}
            id={`edit-${field.id}`}
            leftIcon={FAIconName.penRegular}
            onClick={() => handleSelectField()}
            variant="secondary"
          >
            Edit
          </FireflyButton>
        )}
      </ChakraFlex>
      <ChakraVStack alignItems="flex-start" gap="12px" width="100%">
        {hasFieldValue && (
          <SectionCard id={field.id}>
            <ChakraFlex flexDirection="column" gap="6px" width="100%">
              <EditorV2
                ariaProps={{
                  input: {
                    'aria-labelledby':
                      'referrals-follow-up-appointments-header',
                  },
                }}
                containerTestId={`${field.id}-editor`}
                hideBorder
                initialContent={htmlContent}
                key={htmlContent}
                readOnly
                stateRef={editorRef}
              />
            </ChakraFlex>
          </SectionCard>
        )}
        {isSigned && !hasFieldValue && (
          <Text color="neutral.tertiary" variant="body">
            No details provided
          </Text>
        )}
        {!isSigned && !hasFieldValue && (
          <ChakraFlex flexWrap="wrap" gap="8px">
            <FireflyButton
              ariaLabel={`add-${field.id}`}
              id={`add-${field.id}`}
              key={field.key}
              leftIcon={FAIconName.plusSolid}
              onClick={() => handleSelectField()}
              size="small"
              variant="secondary"
            >
              Add details
            </FireflyButton>
          </ChakraFlex>
        )}
      </ChakraVStack>
    </Section>
  );
};

export default ReferralsAndFollowUpAppointmentsSection;
